import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailCheck, RefreshCw, Check, X as XIcon, Loader2 } from "lucide-react";
import CityPicker from "@/components/CityPicker";
import { isUsernameTaken } from "@/lib/events";

const USERNAME_RE = /^[a-z0-9._-]{3,32}$/;
const GENDERS = ["", "female", "male", "non-binary", "prefer not to say"];

export default function SignupPage() {
  const { signUp, resendConfirmation } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/feed";

  const [form, setForm] = useState({
    username: "",
    full_name: "",
    birthday: "",
    gender: "",
    email: "",
    password: "",
    city: null, // { city, state, country, lat, lng, display }
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // { email }

  // Live username availability check. Debounced 350ms. We only run the
  // check when the local format is already valid — there's no point
  // hitting the DB for "ab" or "Maya P!".
  // States: "idle" | "checking" | "available" | "taken" | "invalid"
  const [usernameStatus, setUsernameStatus] = useState("idle");
  useEffect(() => {
    const u = form.username.trim().toLowerCase();
    if (!u) { setUsernameStatus("idle"); return; }
    if (!USERNAME_RE.test(u)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const taken = await isUsernameTaken(u);
        if (cancelled) return;
        setUsernameStatus(taken ? "taken" : "available");
      } catch {
        // Network blips shouldn't block signup; fall through to the
        // server-side unique constraint as the final safety net.
        if (!cancelled) setUsernameStatus("idle");
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.username]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.full_name.trim()) return "Tell us your full name.";
    const u = form.username.trim().toLowerCase();
    if (!USERNAME_RE.test(u)) {
      return "Username: 3–32 chars, lowercase letters, numbers, '.', '_' or '-'.";
    }
    if (usernameStatus === "taken") {
      return "That username is taken. Pick another.";
    }
    if (usernameStatus === "checking") {
      return "Hold on — checking that username.";
    }
    if (!form.birthday) return "Pick your birthday.";
    if (!form.gender) return "Choose a gender.";
    if (!form.email.trim()) return "We need your email.";
    if (!form.city || !form.city.city) return "Pick your city.";
    if (form.password.length < 6) return "Password must be at least 6 characters.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);

    // Final pre-flight availability check. Closes the 350ms debounce
    // window where another user could have grabbed the same handle while
    // this user was finishing the form. Cheap call (~50ms) and prevents
    // the much worse experience of a half-created auth.user with no
    // matching profile because of the race.
    try {
      const taken = await isUsernameTaken(form.username.trim().toLowerCase());
      if (taken) {
        setUsernameStatus("taken");
        setError("That username is taken. Pick another.");
        setLoading(false);
        return;
      }
    } catch {
      // Network blip: fall through to the server-side unique constraint.
    }

    const res = await signUp({
      email: form.email.trim(),
      password: form.password,
      username: form.username.trim().toLowerCase(),
      full_name: form.full_name.trim(),
      display_name: form.full_name.trim(),
      birthday: form.birthday,
      gender: form.gender,
      city: form.city?.city || null,
      state: form.city?.state || null,
      country: form.city?.country || null,
      location_lat: form.city?.lat ?? null,
      location_lng: form.city?.lng ?? null,
    });
    setLoading(false);
    if (!res.ok) {
      // Server's own race-loss path: surface the username conflict clearly.
      const errStr = String(res.error || "");
      const looksDuplicateUsername =
        /username/i.test(errStr) ||
        /duplicate key|23505/i.test(errStr);
      if (looksDuplicateUsername) {
        setUsernameStatus("taken");
        setError("That username is taken. Pick another.");
        return;
      }
      setError(errStr);
      return;
    }
    if (res.needsConfirm) {
      setDone({ email: form.email.trim() });
      toast.success("Check your inbox to confirm your email.");
      return;
    }
    toast.success("You're in. Welcome.");
    navigate(next);
  };

  const onResend = async () => {
    const res = await resendConfirmation(done?.email);
    if (res.ok) toast.success("Confirmation email re-sent.");
    else toast.error(res.error || "Could not resend.");
  };

  if (done) {
    return (
      <div className="bg-paper min-h-[calc(100vh-4rem)] flex items-center justify-center px-6">
        <div data-testid="signup-check-email" className="w-full max-w-md border border-ink/15 p-8 sm:p-10 bg-grain text-center">
          <div className="w-16 h-16 mx-auto border border-neon flex items-center justify-center mb-6">
            <MailCheck size={28} strokeWidth={1.6} className="text-ink" />
          </div>
          <span className="text-xs uppercase tracking-[0.3em] text-ink">Almost in</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 mb-4 leading-none">
            Check your email.
          </h1>
          <p className="text-ink/70 leading-relaxed">
            We sent a confirmation link to <span className="text-ink">{done.email}</span>.
            Click it to verify your account.
          </p>
          <div className="flex flex-col gap-3 mt-8">
            <button
              type="button"
              onClick={onResend}
              data-testid="resend-confirm"
              className="inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-paper hover:text-ink px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
            >
              <RefreshCw size={14} strokeWidth={2.2} /> Resend confirmation
            </button>
            <Link
              to="/login"
              data-testid="go-login"
              className="text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors"
            >
              Back to log in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-ink/15 bg-grain">
        <Link to="/" className="inline-flex items-center">
          <img src="/inonit-logo.png" alt="inonit" className="h-9 w-auto select-none" draggable={false} />
        </Link>
        <div>
          <p className="font-display text-4xl xl:text-6xl font-bold leading-[0.95] tracking-tight text-ink">
            Show up.<br />
            Say hi.<br />
            <span className="text-ink">That's all.</span>
          </p>
          <p className="text-ink/60 mt-6 max-w-md">One account. No followers. No notifications.</p>
        </div>
        <span className="text-xs uppercase tracking-widest text-ink/40">© inonit</span>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <span className="text-xs uppercase tracking-[0.3em] text-ink">Make an account</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 mb-10 leading-none">
            Join the<br />notice board.
          </h1>

          <form onSubmit={submit} data-testid="signup-form" className="flex flex-col gap-6">
            <Field label="Full name">
              <input type="text" required maxLength={80} value={form.full_name}
                onChange={(e) => setField("full_name", e.target.value)}
                data-testid="signup-fullname" placeholder="Maya Patel" className="chunky-input" />
            </Field>

            <Field label="Username">
              <div className="flex items-center gap-2 border-b border-ink/15 focus-within:border-neon transition-colors">
                <span className="text-ink/50">@</span>
                <input
                  type="text"
                  required
                  maxLength={32}
                  value={form.username}
                  onChange={(e) =>
                    setField("username", e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))
                  }
                  data-testid="signup-username"
                  placeholder="maya.p"
                  className="chunky-input border-0 px-0 flex-1"
                  aria-invalid={usernameStatus === "taken" || usernameStatus === "invalid"}
                />
                {form.username && (
                  <span
                    data-testid={`signup-username-status-${usernameStatus}`}
                    className="shrink-0 inline-flex items-center justify-center w-6 h-6"
                    aria-live="polite"
                  >
                    {usernameStatus === "checking" && (
                      <Loader2 size={16} className="animate-spin text-ink/55" />
                    )}
                    {usernameStatus === "available" && (
                      <Check size={18} strokeWidth={3} className="text-lime-600" />
                    )}
                    {(usernameStatus === "taken" || usernameStatus === "invalid") && (
                      <XIcon size={18} strokeWidth={3} className="text-destructive" />
                    )}
                  </span>
                )}
              </div>
              <span
                className={
                  "text-[11px] mt-1.5 " +
                  (usernameStatus === "taken"
                    ? "text-destructive font-semibold"
                    : usernameStatus === "available"
                    ? "text-lime-700 font-semibold"
                    : "text-ink/45")
                }
                data-testid="signup-username-hint"
              >
                {usernameStatus === "taken"
                  ? "That username is taken. Try another."
                  : usernameStatus === "available"
                  ? `@${form.username} is available.`
                  : usernameStatus === "checking"
                  ? "Checking availability…"
                  : "3–32 lowercase chars · letters, numbers, '.', '_' or '-' · used in your profile URL."}
              </span>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Birthday">
                <input type="date" required value={form.birthday}
                  onChange={(e) => setField("birthday", e.target.value)}
                  data-testid="signup-birthday" className="chunky-input" />
              </Field>
              <Field label="Gender">
                <select required value={form.gender}
                  onChange={(e) => setField("gender", e.target.value)}
                  data-testid="signup-gender" className="chunky-input bg-paper">
                  {GENDERS.map((g) => (
                    <option key={g} value={g} className="bg-paper text-ink">
                      {g ? g.charAt(0).toUpperCase() + g.slice(1) : "Choose…"}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Email">
              <input type="email" required value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                data-testid="signup-email" placeholder="you@yourtown.com" className="chunky-input" />
            </Field>

            <Field label="Your city">
              <CityPicker
                value={form.city}
                onChange={(c) => setField("city", c)}
                testidPrefix="signup-city"
              />
              <span className="text-[11px] text-ink/45 mt-1.5">
                Used to surface events near you in your feed and on the map.
              </span>
            </Field>

            <Field label="Password">
              <input type="password" required minLength={6} value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                data-testid="signup-password" placeholder="At least 6 characters" className="chunky-input" />
            </Field>

            {error && (
              <div data-testid="signup-error" className="text-destructive text-sm border border-destructive/40 px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || usernameStatus === "taken" || usernameStatus === "checking"}
              data-testid="signup-submit"
              className="bg-neon text-ink py-4 font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 mt-2">
              {loading ? "Creating…" : "Create account"}
            </button>

            <div className="text-sm text-ink/60 mt-2">
              Already have one?{" "}
              <Link to="/login" data-testid="link-login" className="text-ink hover:text-ink underline-offset-4 hover:underline">
                Log in.
              </Link>
            </div>
          </form>

          <style>{`
            .bare-input {
              background: transparent; border: none;
              border-bottom: 1px solid hsl(var(--border));
              color: #f4f4f0; padding: 12px 0; width: 100%;
              font-family: inherit; font-size: 1rem; outline: none;
              transition: border-color 200ms;
            }
            .bare-input:focus { border-bottom-color: #d6ff00; border-bottom-width: 2px; }
            .bare-input::placeholder { color: rgba(244,244,240,0.35); }
            select.bare-input { appearance: none; cursor: pointer; padding-right: 2rem;
              background-image: linear-gradient(45deg, transparent 50%, #d6ff00 50%), linear-gradient(-45deg, transparent 50%, #d6ff00 50%);
              background-position: calc(100% - 14px) 18px, calc(100% - 9px) 18px;
              background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
            }
            input[type="date"].bare-input { color-scheme: dark; }
          `}</style>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-widest text-ink/60">{label}</span>
      {children}
    </label>
  );
}
